import {
  decodeBrokerRequest,
  runnerLimits,
  runnerProtocol,
  type BrokerResponse,
  type ProblemRunResult,
} from "./protocol";

export interface BrokerParent {
  readonly postMessage: (message: BrokerResponse, options: WindowPostMessageOptions) => void;
}

export interface BrokerWorker {
  readonly addEventListener: (
    type: "message" | "error",
    listener: (event: MessageEvent) => void,
  ) => void;
  readonly postMessage: (message: unknown) => void;
  readonly terminate: () => void;
}

interface ActiveJob {
  readonly jobId: string;
  readonly origin: string;
  readonly source: BrokerParent;
  readonly worker: BrokerWorker;
  readonly timeout: ReturnType<typeof setTimeout>;
}

const result = (
  status: "cancelled" | "timed_out" | "platform_error",
  durationMs = 0,
  output: ReadonlyArray<string> = [],
): ProblemRunResult => ({
  status,
  diagnostics: [],
  tests: [],
  output,
  durationMs,
});

export const makeRunnerBroker = (options: {
  readonly parent: BrokerParent;
  readonly allowedOrigins: ReadonlySet<string>;
  readonly createWorker: () => BrokerWorker;
  readonly setTimer?: typeof setTimeout;
  readonly clearTimer?: typeof clearTimeout;
}) => {
  const setTimer = options.setTimer ?? setTimeout;
  const clearTimer = options.clearTimer ?? clearTimeout;
  let active: ActiveJob | null = null;
  // Every job must run in a realm no attempt has touched, so a finished Worker can never be
  // reused. Instantiating one costs ~240ms (a ~4.7MB module holding the whole TypeScript
  // compiler), and paying it after the Run click made that the entire perceived run latency.
  // Keep exactly one pristine, never-used spare instead, built while the learner reads or edits.
  let standby: BrokerWorker | null = null;

  const prewarm = () => {
    if (standby !== null) return;
    let worker: BrokerWorker;
    try {
      worker = options.createWorker();
    } catch {
      // On-demand creation in `handleMessage` still reports the failure to the parent.
      return;
    }
    standby = worker;
    // A spare that dies before it is claimed must never be handed to a job.
    worker.addEventListener("error", () => {
      if (standby !== worker) return;
      standby = null;
      worker.terminate();
    });
  };

  const takeWorker = (): BrokerWorker => {
    const warm = standby;
    if (warm === null) return options.createWorker();
    standby = null;
    return warm;
  };

  const send = (source: BrokerParent, origin: string, message: BrokerResponse) => {
    try {
      source.postMessage(message, { targetOrigin: origin });
    } catch {
      return;
    }
  };

  const finish = (job: ActiveJob, runResult: ProblemRunResult) => {
    if (active !== job) return;
    active = null;
    clearTimer(job.timeout);
    job.worker.terminate();
    send(job.source, job.origin, {
      protocol: runnerProtocol,
      kind: "result",
      jobId: job.jobId,
      result: runResult,
    });
    prewarm();
  };

  const handleMessage = (event: {
    readonly origin: string;
    readonly source: unknown;
    readonly data: unknown;
  }) => {
    if (event.source !== options.parent || !options.allowedOrigins.has(event.origin)) return;
    const request = decodeBrokerRequest(event.data);
    if (request === null) return;

    if (request.kind === "cancel") {
      if (active?.jobId === request.jobId) finish(active, result("cancelled"));
      return;
    }

    if (active !== null) finish(active, result("cancelled"));

    let worker: BrokerWorker;
    try {
      worker = takeWorker();
    } catch {
      send(options.parent, event.origin, {
        protocol: runnerProtocol,
        kind: "result",
        jobId: request.jobId,
        result: result("platform_error", 0, [
          "The isolated execution Worker could not be created.",
        ]),
      });
      return;
    }

    const timeoutMs = Math.max(250, Math.min(request.request.timeoutMs, runnerLimits.timeoutMs));
    const job: ActiveJob = {
      jobId: request.jobId,
      origin: event.origin,
      source: options.parent,
      worker,
      timeout: setTimer(() => finish(job, result("timed_out", timeoutMs)), timeoutMs),
    };
    active = job;

    send(job.source, job.origin, {
      protocol: runnerProtocol,
      kind: "phase",
      jobId: job.jobId,
      phase: "compiling",
    });

    worker.addEventListener("message", (workerEvent) => {
      const data = workerEvent.data as {
        readonly jobId?: unknown;
        readonly phase?: unknown;
        readonly result?: unknown;
      };
      if (active !== job || data.jobId !== job.jobId) return;
      if (data.phase === "running") {
        send(job.source, job.origin, {
          protocol: runnerProtocol,
          kind: "phase",
          jobId: job.jobId,
          phase: "running",
        });
        return;
      }
      if (typeof data.result !== "object" || data.result === null) return;
      finish(job, data.result as ProblemRunResult);
    });
    worker.addEventListener("error", () => {
      finish(
        job,
        result("platform_error", 0, ["The isolated execution Worker stopped unexpectedly."]),
      );
    });

    try {
      worker.postMessage({
        jobId: job.jobId,
        request: request.request,
      });
    } catch {
      finish(
        job,
        result("platform_error", 0, ["The isolated execution Worker could not start the run."]),
      );
    }
  };

  return {
    handleMessage,
    prewarm,
    dispose: () => {
      const spare = standby;
      standby = null;
      spare?.terminate();
      if (active === null) return;
      const job = active;
      active = null;
      clearTimer(job.timeout);
      job.worker.terminate();
    },
  } as const;
};
